import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  FileText,
  ListFilter,
  MessageSquare,
  Quote,
  Search,
  ShieldCheck,
  TrendingUp,
  UserPlus,
  Users,
  Video,
} from 'lucide-react';
import CustomerInsights from './CustomerInsights';
import CaseStudyChallenge from './CaseStudyChallenge';

const CTA_HREF = '/hirewithus/book-demo';
const CTA_LABEL = 'Experience SearchJob.ai with us';

const buttonPrimary =
  'inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

function useSeen(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, seen];
}

const JOURNEY_MS = 12500;

function stageAt(t) {
  if (t < 0.065) return { card: 0, arrow: -1 };
  if (t < 0.09) return { card: -1, arrow: 0 };
  if (t < 0.155) return { card: 1, arrow: -1 };
  if (t < 0.18) return { card: -1, arrow: 1 };
  if (t < 0.245) return { card: 2, arrow: -1 };
  if (t < 0.27) return { card: -1, arrow: 2 };
  if (t < 0.36) return { card: 3, arrow: -1 };
  if (t < 0.82) return { card: -1, arrow: -1 };
  if (t < 0.845) return { card: -1, arrow: 3 };
  if (t < 0.9) return { card: 4, arrow: -1 };
  if (t < 0.925) return { card: -1, arrow: 4 };
  if (t < 0.955) return { card: 5, arrow: -1 };
  return { card: 6, arrow: -1 };
}

function useJourneyPlay(threshold = 0.22) {
  const ref = useRef(null);
  const [phase, setPhase] = useState('still');
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setPhase((current) => {
          if (entry.isIntersecting) return 'play';
          if (current === 'play' || current === 'pause') return 'pause';
          return current;
        });
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, phase];
}

function paintJourney(root, t) {
  const track = root.querySelector('.gg-wtrack');
  const cards = track ? [...track.querySelectorAll('.gg-wcard')] : [];
  const arrows = track ? [...track.querySelectorAll('.gg-nudge')] : [];
  const { card, arrow } = stageAt(t);
  cards.forEach((el, index) => el.classList.toggle('is-hot', index === card));
  arrows.forEach((el, index) => el.classList.toggle('is-hot', index === arrow));

  const runner = root.querySelector('.gg-runner');
  if (runner && cards.length) {
    const target = card >= 0 ? card : arrow >= 0 ? Math.min(arrow + 1, cards.length - 1) : -1;
    const hide = (t >= 0.32 && t < 0.82) || target < 0;
    runner.classList.toggle('is-hidden', hide);
    if (target >= 0 && cards[target]) {
      const wrapped = (root._ggT || 0) > 0.92 && t < 0.08;
      runner.classList.toggle('is-wrap', wrapped);
      const x = cards[target].offsetLeft + cards[target].offsetWidth / 2 - 3;
      runner.style.transform = `translateX(${x}px)`;
    }
  }

  root._ggT = t;
}

function useJourneyLoop(ref, phase) {
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const armed = phase !== 'still';
  useEffect(() => {
    if (!armed) return undefined;
    const root = ref.current;
    const clock = root && root.querySelector('.gg-clock');
    if (!root || !clock) return undefined;
    const anim = clock.animate([{ opacity: 1 }, { opacity: 1 }], { duration: JOURNEY_MS, iterations: Infinity });
    let frame = 0;
    const tick = () => {
      const mode = phaseRef.current;
      if (mode === 'pause' && anim.playState === 'running') anim.pause();
      if (mode === 'play') {
        if (anim.playState === 'paused') anim.play();
        paintJourney(root, ((anim.currentTime || 0) % JOURNEY_MS) / JOURNEY_MS);
      }
      frame = requestAnimationFrame(tick);
    };
    const observer = new ResizeObserver(() => {
      root._ggNodes = null;
      root._ggBox = null;
    });
    observer.observe(root);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      anim.cancel();
    };
  }, [armed, ref]);
}

const stageIcons = {
  Identify: Search,
  Screen: ListFilter,
  Interview: Video,
  Communicate: MessageSquare,
  Offer: FileText,
  Join: UserPlus,
  Settle: ShieldCheck,
};

const iconByKey = {
  search: Search,
  screen: ListFilter,
  interview: Video,
  message: MessageSquare,
  offer: FileText,
  join: UserPlus,
  settle: ShieldCheck,
};

const incomingRows = [
  ['70%', '42%'],
  ['54%', '36%'],
  ['62%', '30%'],
];

function emphasisFor(label, study) {
  if ((study.successStages || []).includes(label)) return 'strong';
  if ((study.highlightStages || []).includes(label)) return 'soft';
  return 'muted';
}

function SurgeBoard({ study }) {
  const [ref, seen] = useSeen(0.28);
  if (study.heroVisualVariant !== 'hiring-surge') return null;
  const pipeline = study.pipelineStages || [];
  const logo = study.heroLogo || study.assets.logo;
  return (
    <figure ref={ref} className={`gg-board h-auto min-w-0 self-start rounded-[24px] border border-border bg-white p-5 shadow-sm lg:p-3 ${seen ? 'is-seen' : ''}`}>
      <div className="flex items-center gap-3 lg:gap-2.5">
        <div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-xl border border-border bg-white px-3 py-2 shadow-sm lg:h-12 lg:py-1.5">
          <img src={logo} alt="" className="h-10 w-auto max-w-full object-contain lg:h-8" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight text-foreground">{study.heroVisualTitle}</p>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{study.heroVisualSubtitle}</p>
        </div>
      </div>

      <div className="relative mt-4 grid items-center gap-3 lg:mt-2 lg:gap-2.5 lg:grid-cols-[6.75rem_minmax(0,1fr)]">
        <span className="gg-bridge pointer-events-none absolute left-[6.4rem] top-1/2 hidden w-3 bg-primary/40 lg:block" aria-hidden="true" />
        <div className="gg-in overflow-hidden rounded-xl border border-border bg-[hsl(158_45%_98%)] px-2 py-2 lg:py-1.5">
          <p className="text-[11px] font-semibold leading-tight text-foreground">{study.heroIncomingLabel}</p>
          <div className="mt-1.5 space-y-1" aria-hidden="true">
            {incomingRows.map(([bar, line]) => (
              <div key={bar} className="gg-in-row flex items-center gap-1.5">
                <span className="h-4 w-4 shrink-0 rounded-full border border-primary/15 bg-white" />
                <span className="h-1.5 rounded-full bg-primary/20" style={{ width: bar }} />
                <span className="gg-status h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span className="hidden h-1.5 rounded-full bg-primary/10 sm:block" style={{ width: line }} />
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">{study.heroIncomingMore}</p>
        </div>

        <div className="min-w-0">
          <div className="overflow-x-auto">
            <div className="flex min-w-[34rem] items-stretch gap-0.5 lg:min-w-0">
              {pipeline.map((label, index) => {
                const Icon = stageIcons[label] || Search;
                const tone = emphasisFor(label, study);
                return (
                  <React.Fragment key={label}>
                    <div
                      data-i={index}
                      style={{ '--i': index }}
                      className={`gg-stage ${tone === 'strong' ? 'gg-strong' : ''} ${tone === 'soft' ? 'gg-soft' : ''} flex min-w-[4.15rem] flex-1 flex-col items-center gap-1 rounded-lg border px-1 py-1.5 text-center ${
                        tone === 'strong'
                          ? 'border-primary/40 bg-[hsl(152_40%_96%)]'
                          : tone === 'soft'
                            ? 'border-primary/25 bg-[hsl(158_45%_97%)]'
                            : 'border-border bg-white'
                      }`}
                    >
                      <Icon className="h-3 w-3 text-primary" aria-hidden="true" />
                      <span className="whitespace-nowrap text-[10px] font-semibold leading-none text-foreground">{label}</span>
                      <span className={`gg-dot h-1 w-1 rounded-full ${tone === 'muted' ? 'bg-primary/25' : 'bg-primary/70'}`} />
                    </div>
                    {index < pipeline.length - 1 ? <span className="gg-harrow flex items-center text-[10px] text-primary/35" aria-hidden="true">→</span> : null}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 lg:mt-2">
        <MessageSquare className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold leading-tight text-foreground">{study.communicationTitle}</p>
          <div className="relative mt-1.5 h-2" aria-hidden="true">
            <span className="gg-line absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-primary/30" />
            <span className="gg-travel absolute top-1/2 z-10 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary" />
            <span className="relative flex">
              {pipeline.map((label) => {
                const tone = emphasisFor(label, study);
                return (
                  <span key={label} className="flex flex-1 justify-center">
                    <span className={`block rounded-full ${tone === 'muted' ? 'h-1.5 w-1.5 bg-primary/35' : 'h-2 w-2 bg-primary'}`} />
                  </span>
                );
              })}
            </span>
          </div>
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{study.communicationText}</p>
        </div>
      </div>

      <div className="mt-4 grid items-stretch gap-2 sm:grid-cols-3 lg:mt-2">
        {(study.heroInsightCards || []).map((card) => (
          <div key={card.title} className={`gg-insight gg-vis-${card.visual} rounded-xl border border-border bg-[hsl(158_45%_98.5%)] px-2.5 py-2 lg:px-2 lg:py-1.5`}>
            <InsightMark visual={card.visual} />
            <p className="mt-1.5 text-[11px] font-semibold leading-tight text-foreground">{card.title}</p>
            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{card.description}</p>
          </div>
        ))}
      </div>
      <style>{`
        .gg-line, .gg-in-row, .gg-bridge, .gg-harrow { transition: transform 400ms ease; }
        .gg-line, .gg-bridge { transform: scaleX(0); transform-origin: left center; }
        .gg-bridge { top: 50%; height: 1px; margin-top: -0.5px; }
        .gg-board.is-seen .gg-line { transform: scaleX(1); transition: transform 700ms ease; }
        .gg-board.is-seen .gg-bridge { transform: scaleX(1); transition: transform 500ms ease; }
        .gg-board.is-seen .gg-in-row { transform: translateX(2px); }
        .gg-dot, .gg-mile-dot { opacity: 0.28; }
        .gg-board.is-seen .gg-dot, .gg-board.is-seen .gg-mile-dot { opacity: 1; transition: opacity 280ms ease, transform 280ms ease; transition-delay: calc(var(--i) * 80ms); }
        .gg-board.is-seen .gg-status { animation: gg-dot-once 520ms ease; }
        .gg-travel { left: 0; opacity: 0; }
        .gg-board.is-seen .gg-travel { animation: gg-travel 4.8s ease-in-out 700ms infinite; }
        .gg-stage, .gg-insight, .gg-harrow, .gg-mile-dot { transition: transform 220ms ease, background-color 220ms ease, border-color 220ms ease, opacity 220ms ease; }
        .gg-match-a, .gg-match-b, .gg-jline { transition: transform 400ms ease; }
        .gg-jline { transform: scaleX(0.35); transform-origin: left center; }
        @media (hover: hover) and (pointer: fine) {
          .gg-in:hover .gg-in-row { transform: translateX(4px); }
          .gg-in:hover .gg-status { animation: gg-dot-once 520ms ease; }
          .gg-stage:hover, .gg-insight:hover { transform: translateY(-2px); }
          .gg-stage:hover svg { color: hsl(158 42% 26%); }
          .gg-stage:hover + .gg-harrow { transform: translateX(3px); color: hsl(165 37% 22%); }
          .gg-soft:hover { background-color: hsl(158 45% 93%); border-color: hsl(165 37% 22% / 0.42); }
          .gg-strong:hover { background-color: hsl(152 40% 92%); border-color: hsl(165 37% 22% / 0.55); }
          .gg-mile:hover .gg-mile-dot { transform: scale(1.2); }
          .gg-board:has(.gg-mile:hover) .gg-stage { opacity: 0.8; }
          .gg-board:has(.gg-mile[data-i="0"]:hover) .gg-stage[data-i="0"],
          .gg-board:has(.gg-mile[data-i="1"]:hover) .gg-stage[data-i="1"],
          .gg-board:has(.gg-mile[data-i="2"]:hover) .gg-stage[data-i="2"],
          .gg-board:has(.gg-mile[data-i="3"]:hover) .gg-stage[data-i="3"],
          .gg-board:has(.gg-mile[data-i="4"]:hover) .gg-stage[data-i="4"],
          .gg-board:has(.gg-mile[data-i="5"]:hover) .gg-stage[data-i="5"],
          .gg-board:has(.gg-mile[data-i="6"]:hover) .gg-stage[data-i="6"] { opacity: 1; background-color: hsl(158 45% 94%); }
          .gg-vis-match:hover .gg-match-a { transform: translateX(4px); }
          .gg-vis-match:hover .gg-match-b { transform: translateX(2px); background-color: hsl(158 42% 80%); }
          .gg-vis-journey:hover .gg-jline { transform: scaleX(1); }
          .gg-vis-pipeline:hover .gg-bar-end { transform: scaleX(1.12); transform-origin: left center; }
        }
        @keyframes gg-dot-once { 45% { transform: scale(1.35); } }
        @keyframes gg-travel {
          0% { left: 0; opacity: 0; }
          12% { opacity: 1; }
          88% { opacity: 1; }
          100% { left: calc(100% - 6px); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .gg-board.is-seen .gg-line,
          .gg-board.is-seen .gg-bridge,
          .gg-board.is-seen .gg-in-row,
          .gg-board.is-seen .gg-travel,
          .gg-board.is-seen .gg-status,
          .gg-board.is-seen .gg-dot,
          .gg-board.is-seen .gg-mile-dot,
          .gg-stage:hover,
          .gg-insight:hover,
          .gg-harrow,
          .gg-match-a,
          .gg-match-b,
          .gg-jline,
          .gg-bar-end {
            animation: none;
            transition: none;
            transform: none;
            opacity: 1;
          }
          .gg-board.is-seen .gg-travel { left: calc(100% - 6px); }
        }
      `}</style>
    </figure>
  );
}

function InsightMark({ visual }) {
  if (visual === 'pipeline') {
    return (
      <span className="flex h-4 items-center gap-1" aria-hidden="true">
        <span className="h-1.5 w-3 rounded-full bg-primary/20" />
        <span className="h-1.5 w-1.5 rounded-full bg-primary/35" />
        <span className="gg-bar-end h-1.5 w-5 rounded-full bg-primary/55" />
      </span>
    );
  }
  if (visual === 'match') {
    return (
      <span className="flex h-4 items-center gap-1" aria-hidden="true">
        <span className="gg-match-a h-3.5 w-3.5 rounded-full border border-primary/25 bg-white" />
        <span className="h-px w-2.5 bg-primary/30" />
        <span className="gg-match-b h-3.5 w-3.5 rounded-full bg-primary/15" />
      </span>
    );
  }
  return (
    <span className="flex h-4 items-center" aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <span key={index} className="flex items-center">
          <span className={`h-1.5 w-1.5 rounded-full ${index === 3 ? 'bg-primary' : 'bg-primary/35'}`} />
          {index < 3 ? <span className="gg-jline mx-0.5 h-px w-2 bg-primary/25" /> : null}
        </span>
      ))}
    </span>
  );
}

function Card({ title, body, children, icon: Icon }) {
  return (
    <article className="flex h-full flex-col rounded-[20px] border border-border bg-[hsl(158_45%_98%)] p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
      {Icon ? (
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
      <h3 className={`${Icon ? 'mt-3' : ''} text-base font-semibold text-foreground`}>{title}</h3>
      {body ? <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p> : null}
      {children ? <div className="mt-4">{children}</div> : null}
    </article>
  );
}

export default function GrandGamingStudy({ study }) {
  const [flowRef, flowPhase] = useJourneyPlay(0.22);
  useJourneyLoop(flowRef, flowPhase);
  const flowLive = flowPhase === 'play' || flowPhase === 'pause';
  const { lead, rest } = splitQuote(study.quote);
  return (
    <>
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1440px] items-start gap-6 px-4 pb-8 pt-6 sm:px-6 lg:grid-cols-[minmax(0,32rem)_minmax(0,1fr)] lg:items-start lg:gap-8 lg:px-8 lg:pb-8 lg:pt-8">
          <div className="flex min-w-0 flex-col self-start">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{study.category}</p>
            <div className="mt-3 flex items-center gap-3 sm:gap-4">
              <div className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-white px-2 py-1 shadow-sm">
                <img src={study.heroLogo || study.assets.logo} alt="" className="h-8 w-auto max-w-full object-contain" />
              </div>
              <p className="text-2xl font-bold leading-tight tracking-[-0.03em] text-foreground sm:text-3xl">{study.customer}</p>
            </div>
            <h1 className="mt-6 max-w-xl text-2xl font-bold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-[1.7rem]">{study.headline}</h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">{study.context}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {(study.heroTags || []).map((item) => (
                <li key={item} className="rounded-full border border-primary/20 bg-[hsl(158_45%_98%)] px-3 py-1 text-xs font-semibold text-primary">{item}</li>
              ))}
            </ul>
          </div>
          <div className="min-w-0 lg:self-center">
            <SurgeBoard study={study} />
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Customer at a glance</p>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {study.glance.map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-border bg-card px-5 py-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">{label}</p>
                <p className="mt-2 text-lg font-semibold text-foreground">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CaseStudyChallenge study={study} />

      <section className="border-b border-border">
        <div ref={flowRef} className={`gg-flow relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 ${flowLive ? 'is-live' : ''} ${flowPhase === 'pause' ? 'is-paused' : ''}`}>
          <span className="gg-clock" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">How the workflow changed</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
            From first contact to settling in.
          </h2>
          <p className="mt-4 w-full max-w-none text-base leading-relaxed text-muted-foreground">{study.workflowIntro}</p>
          <div className="mt-6 w-full rounded-2xl border border-primary/20 bg-[hsl(152_42%_96%)] p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-center gap-3" role="list" aria-label="Hiring workflow stages">
              {(study.workflowStages || []).map((step, index, list) => (
                <React.Fragment key={step.key}>
                  <span className="flex h-12 items-center rounded-xl border border-primary/15 bg-white px-4 text-sm font-medium text-foreground" role="listitem">
                    {step.label}
                  </span>
                  {index < list.length - 1 ? (
                    <span
                      className="gg-flow-connector relative hidden h-px w-8 shrink-0 overflow-hidden bg-primary/25 sm:block"
                      style={{ '--gg-flow-delay': `${index * 180}ms` }}
                      aria-hidden="true"
                    >
                      <span className="gg-flow-connector-dot absolute left-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary" />
                    </span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </div>
          <style>{`
            @keyframes gg-flow-travel {
              0%, 14% { transform: translate(-100%, -50%); opacity: 0; }
              22%, 72% { opacity: 1; }
              86%, 100% { transform: translate(500%, -50%); opacity: 0; }
            }
            .gg-flow-connector-dot {
              animation: gg-flow-travel 2.4s ease-in-out infinite;
              animation-delay: var(--gg-flow-delay);
            }
            @media (prefers-reduced-motion: reduce) {
              .gg-flow-connector-dot { animation: none; transform: translate(250%, -50%); opacity: 1; }
            }
          `}</style>
          {false && (
            <>
          <div className="mt-6 rounded-2xl border border-primary/15 bg-[hsl(158_45%_98%)] p-2.5 sm:p-3">
            <div className="overflow-x-auto pb-1">
              <div className="gg-wtrack relative flex min-w-[51rem] items-stretch justify-center gap-1.5 lg:min-w-0">
              <span className="gg-runner" aria-hidden="true" />
              {(study.workflowStages || []).map((step, index, list) => {
                const Icon = iconByKey[step.icon] || Search;
                const tone = step.success ? 'gg-success' : step.emphasis ? 'gg-soft' : '';
                return (
                  <React.Fragment key={step.key}>
                    <article className={`gg-wcard ${tone} flex w-[6.75rem] shrink-0 flex-col items-center rounded-xl border px-2 py-3 text-center`}>
                      <span className={`gg-wicon flex h-9 w-9 items-center justify-center rounded-xl ${step.emphasis ? 'gg-bubbles' : ''}`}>
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="mt-2 whitespace-nowrap text-xs font-semibold text-foreground">{step.label}</span>
                      {step.success ? <span className="gg-done mt-1.5 h-1.5 w-5 rounded-full bg-primary" aria-hidden="true" /> : <span className="mt-1.5 h-1.5 w-5" aria-hidden="true" />}
                    </article>
                    {index < list.length - 1 ? (
                      <span className="gg-nudge flex items-center px-0.5 text-primary/40" aria-hidden="true">
                        <span className="gg-nudge-glyph">→</span>
                      </span>
                    ) : null}
                  </React.Fragment>
                );
              })}
              </div>
            </div>
          </div>

          <style>{`
            .gg-clock { position: absolute; width: 0; height: 0; overflow: hidden; pointer-events: none; }
            .gg-wcard { border-color: hsl(164 20% 90%); background: #fff; transition: transform 280ms ease, border-color 280ms ease, background-color 280ms ease, box-shadow 280ms ease; }
            .gg-wicon { background: hsl(158 45% 96%); color: hsl(165 37% 22%); transition: background-color 350ms ease, color 350ms ease; }
            .gg-soft { border-color: hsl(165 37% 22% / 0.28); background: hsl(158 45% 97%); }
            .gg-success { border-color: hsl(165 37% 22% / 0.45); background: hsl(152 40% 96%); }
            .gg-runner { position: absolute; z-index: 2; top: 8px; left: 0; width: 6px; height: 6px; border-radius: 999px; background: hsl(165 37% 22%); pointer-events: none; opacity: 0; transition: transform 420ms ease, opacity 200ms ease; }
            .gg-flow.is-live .gg-runner { opacity: 1; }
            .gg-runner.is-wrap { transition: none; }
            .gg-flow.is-live .gg-runner.is-hidden { opacity: 0; }
            .gg-nudge { position: relative; color: hsl(165 37% 22% / 0.4); }
            .gg-nudge-glyph { display: inline-block; transition: translate 300ms ease; }
            .gg-nudge::after { content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 1px; background: hsl(165 37% 22% / 0.35); transform: scaleX(0); pointer-events: none; }
            .gg-flow.is-live .gg-wcard.is-hot { border-color: hsl(165 37% 22% / 0.5); box-shadow: 0 8px 16px hsl(165 37% 22% / 0.06); }
            .gg-flow.is-live .gg-wcard.gg-success.is-hot { border-color: hsl(165 37% 22% / 0.65); background: hsl(152 42% 94%); }
            .gg-flow.is-live .gg-wcard.is-hot .gg-wicon { background: hsl(152 45% 88%); }
            .gg-flow.is-live .gg-nudge.is-hot { color: hsl(165 37% 22%); }
            .gg-flow.is-live .gg-nudge.is-hot .gg-nudge-glyph { translate: 5px 0; }
            .gg-flow.is-live .gg-nudge.is-hot::after { animation: gg-shine 300ms ease; }
            .gg-draw, .gg-arrow { fill: none; stroke: hsl(158 36% 42%); stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }
            .gg-draw { stroke-dasharray: 1; stroke-dashoffset: 1; }
            .gg-glow { fill: none; stroke: hsl(158 42% 34%); stroke-width: 2.25; stroke-linecap: round; stroke-dasharray: 1; stroke-dashoffset: 1; opacity: 0; }
            .gg-arrow { opacity: 0; }
            .gg-flow.is-live .gg-draw { stroke-dashoffset: 0; transition: stroke-dashoffset 1100ms ease; }
            .gg-flow.is-live .gg-arrow { opacity: 1; transition: opacity 200ms ease 900ms; }
            .gg-traveler { position: absolute; left: 0; top: 0; z-index: 3; width: 7px; height: 7px; border-radius: 999px; background: hsl(158 45% 32%); box-shadow: 0 0 0 3px hsl(152 45% 90%); pointer-events: none; opacity: 0; }
            .gg-drop { position: absolute; top: -0.7rem; z-index: 2; width: 7px; height: 7px; border-right: 2px solid hsl(165 37% 22%); border-bottom: 2px solid hsl(165 37% 22%); opacity: 0; pointer-events: none; transform: translate(-50%, 0) rotate(45deg); transition: transform 300ms ease, opacity 300ms ease; }
            .gg-drop.is-on { opacity: 1; transform: translate(-50%, 4px) rotate(45deg); }
            .gg-node { opacity: 0.3; transition: opacity 300ms ease, box-shadow 300ms ease; }
            .gg-flow.is-live .gg-node { opacity: 1; transition-delay: calc(var(--i) * 140ms); }
            .gg-flow.is-live .gg-scene.is-hot .gg-node { box-shadow: 0 0 0 4px hsl(152 45% 80% / 0.9); }
            .gg-pic, .gg-settle, .gg-pulse { transition: transform 280ms ease, background-color 400ms ease, border-color 400ms ease, box-shadow 400ms ease; }
            .gg-list span { opacity: 0.15; }
            .gg-flow.is-live .gg-list span { opacity: 1; transition: opacity 280ms ease; }
            .gg-flow.is-live .gg-list span:nth-child(1) { transition-delay: 520ms; }
            .gg-flow.is-live .gg-list span:nth-child(2) { transition-delay: 660ms; }
            .gg-flow.is-live .gg-list span:nth-child(3) { transition-delay: 800ms; }
            .gg-offer-check { opacity: 0; }
            .gg-flow.is-live .gg-msg { opacity: 0; }
            .gg-flow.is-live .gg-scene.is-lit .gg-msg { animation: gg-msg 420ms ease forwards; }
            .gg-flow.is-live .gg-scene.is-lit .gg-msg:last-child { animation-delay: 170ms; }
            .gg-flow.is-live .gg-scene.is-lit .gg-offer-check { opacity: 1; transition: opacity 300ms ease; }
            .gg-flow.is-live .gg-scene-interview.is-hot .gg-live-dot { animation: gg-live 500ms ease; }
            .gg-flow.is-live .gg-scene-offer.is-hot .gg-pulse { background: hsl(152 45% 94%); border-color: hsl(165 37% 22% / 0.55); }
            .gg-flow.is-live .gg-scene-join.is-hot .gg-pic { animation: gg-confirm 500ms ease; }
            .gg-flow.is-live .gg-scene-settle.is-hot .gg-pic { border-color: hsl(165 37% 22% / 0.72); background: hsl(152 46% 91%); }
            .gg-flow.is-live .gg-scene-settle.is-hot .gg-settle { background: hsl(152 45% 88%); }
            @media (hover: hover) and (pointer: fine) {
              .gg-wcard:hover { transform: translateY(-3px); border-color: hsl(165 37% 22% / 0.48); box-shadow: 0 8px 16px hsl(164 20% 20% / 0.05); }
              .gg-wcard:hover .gg-wicon { background: hsl(152 45% 90%); }
              .gg-wcard:hover + .gg-nudge { color: hsl(165 37% 22%); }
              .gg-wcard:hover + .gg-nudge .gg-nudge-glyph { translate: 5px 0; }
              .gg-wcard:hover + .gg-nudge::after { animation: gg-shine 300ms ease; }
              .gg-scene:hover .gg-pic { transform: translateY(-3px); border-color: hsl(165 37% 22% / 0.5); }
              .gg-scene:hover .gg-node { animation: gg-node-pulse 700ms ease; }
              .gg-scene:hover h3 { color: hsl(165 37% 22%); }
              .gg-scene-settle:hover .gg-settle { background: hsl(158 45% 94%); }
            }
            @keyframes gg-shine { from { transform: scaleX(0); opacity: 0.35; } to { transform: scaleX(1); opacity: 0; } }
            @keyframes gg-msg { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
            @keyframes gg-live { 50% { transform: scale(1.7); } }
            @keyframes gg-confirm { 45% { box-shadow: 0 0 0 5px hsl(152 48% 72% / 0.45); } }
            @keyframes gg-node-pulse { from { box-shadow: 0 0 0 0 hsl(158 45% 42% / 0.45); } to { box-shadow: 0 0 0 7px hsl(158 45% 42% / 0); } }
            .gg-flow.is-paused .gg-msg,
            .gg-flow.is-paused .gg-live-dot,
            .gg-flow.is-paused .gg-scene-join.is-hot .gg-pic,
            .gg-flow.is-paused .gg-nudge::after,
            .gg-flow.is-paused .gg-scene:hover .gg-node { animation-play-state: paused; }
            @media (max-width: 1023px) { .gg-drop { display: none; } }
            @media (prefers-reduced-motion: reduce) {
              .gg-runner, .gg-traveler, .gg-drop, .gg-glow, .gg-offer-check { display: none; }
              .gg-draw { stroke-dashoffset: 0; }
              .gg-arrow, .gg-node, .gg-msg, .gg-list span { opacity: 1; }
              .gg-flow.is-live .gg-msg { opacity: 1; animation: none; }
              .gg-wcard, .gg-wicon, .gg-nudge, .gg-nudge-glyph, .gg-pic, .gg-scene, .gg-node, .gg-settle, .gg-pulse, .gg-live-dot {
                transition: none;
                animation: none;
                transform: none;
              }
            }
          `}</style>
            </>
          )}
        </div>
      </section>

      {false && (
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mt-5 grid gap-4 lg:grid-cols-3">
            <Card title="Hiring pipeline" body="Candidates move through one structured pipeline to join and settle.">
              <MiniFlow labels={['Candidates', 'Pipeline', 'Join / Settle']} lastStrong />
            </Card>
            <Card title="Candidate matching" body="Profiles are compared with job requirements. The matched profiles stay strongest.">
              <ul className="space-y-1.5">
                {['Candidate profile', 'Job requirements', 'Matching'].map((item) => (
                  <li key={item} className="rounded-lg border border-border bg-white px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground">{item}</li>
                ))}
                <li className="rounded-lg border border-primary/30 bg-white px-2.5 py-2 text-[11px] font-semibold text-foreground">Suitable candidates</li>
              </ul>
            </Card>
            <Card title="Candidate communication" body="Messages continue from the interview through settling in.">
              <MiniFlow labels={['Reminder', 'Follow-up', 'Offer', 'Joining', 'Settling']} lastStrong />
            </Card>
          </div>
        </div>
      </section>
      )}

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">The result</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">{study.resultsTitle}</h2>
          <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">{study.resultsIntro}</p>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <Card title="Hiring at scale" body="Hiring requests move into an organized workflow and a stable delivery.">
              <MiniFlow labels={['Requests', 'Workflow', 'Stable delivery']} lastStrong />
            </Card>
            <Card title="Candidate matching" body="A candidate pool is matched to requirements, then to best-fit candidates.">
              <MiniFlow labels={['Candidate pool', 'Requirements', 'Best fit']} lastStrong />
            </Card>
            <Card title="Candidate experience" body="Communication indicators continue across the whole journey.">
              <MiniFlow labels={['Interview', 'Communicate', 'Offer', 'Join', 'Settle']} lastStrong />
            </Card>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Why it matters</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">{study.why.title}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {study.why.points.map((point) => (
              <Card key={point.label} title={point.label} body={point.body} />
            ))}
          </div>
        </div>
      </section>

      <CustomerInsights study={study} />

      <section className="border-b border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-12 text-center sm:px-6 lg:px-8">
          <h2 className="max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
            Want to see what this workflow would look like for your hiring team?
          </h2>
          <a href={CTA_HREF} className={`${buttonPrimary} mt-6 w-full sm:w-auto`}>
            {CTA_LABEL}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      {false && <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Customer insights</h2>
            <span className="h-px w-14 bg-primary/35" aria-hidden="true" />
          </div>
          <figure className="mt-5 grid overflow-hidden rounded-[28px] border border-[hsl(158_28%_82%)] bg-[hsl(158_45%_98%)] shadow-sm lg:grid-cols-[minmax(0,1.7fr)_minmax(220px,0.8fr)]">
            <div className="px-6 py-7 sm:px-10">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true"><Quote className="h-4 w-4" /></span>
              <blockquote className="mt-5">
                <p className="max-w-3xl text-xl font-bold leading-snug tracking-[-0.02em] text-foreground">“{lead}”</p>
                {rest ? <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">“{rest}”</p> : null}
              </blockquote>
              <div className="mt-6 h-px bg-primary/15" />
              <figcaption className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">{study.attribution}</figcaption>
            </div>
            <div className="border-t border-[hsl(158_28%_86%)] bg-[hsl(158_42%_96%)] px-6 py-7 lg:border-l lg:border-t-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">What they valued</p>
              <ul className="mt-4 space-y-2">
                {study.pdfChips.map((item) => (
                  <li key={item} className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm font-medium text-foreground">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </figure>
        </div>
      </section>}

    </>
  );
}

function MiniFlow({ labels, lastStrong }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          <span className={`rounded-lg border px-2 py-1 text-[11px] font-semibold ${lastStrong && index === labels.length - 1 ? 'border-primary/35 bg-white text-foreground' : 'border-border bg-white text-muted-foreground'}`}>{label}</span>
          {index < labels.length - 1 ? <span className="text-primary/50" aria-hidden="true">→</span> : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function splitQuote(quote) {
  const text = String(quote || '').trim();
  const match = text.match(/^(.+?[.!?])\s+([\s\S]+)$/);
  if (!match) return { lead: text, rest: '' };
  return { lead: match[1], rest: match[2].trim() };
}
